import {
    filterByType,
    filterByScore,
    defaultDerivedScore,
    getTopKTags,
    typeMapToCSV,
    transformToTypeMap,
    translateTypes,
    parsePreferenceTags,
    combineTags,
    scoreContentTags,
    getTopKContextTagTypes,
} from "../util/tags";

import {
    getTopKAuthors,
    parsePreferenceAuthors,
} from "../util/authors";

import {
    getTopKResourceTypes,
    parsePreferenceResourceTypes,
} from "../util/resourceTypes";

import transformReferrer from "../util/transformReferrer";
import pluckUserAttributeParams from "../util/pluckUserAttributeParams";

export {
    makeTypeMaps,
    makeParams,
};

function makeParams(args) {
    const {
        userInfo = {},
        contextInfo: {
            referrer,
        } = {},
    } = args;

    const {
        contextTagsTypeMap,
        preferredTagsTypeMap,
        preferredResourceTypesMap,
        cumulativeScoredContentTagsMap,
    } = makeTypeMaps(args);

    const contextTagsCSV = applyTransforms(contextTagsTypeMap, [
        translateTypes,
        typeMapToCSV,
    ]);

    const contextTagTypesCSV = applyTransforms(cumulativeScoredContentTagsMap, [
        translateTypes,
        typeMapToCSV,
    ]);

    const allPreferredMap = Object.assign({}, preferredTagsTypeMap, preferredResourceTypesMap);
    const allPreferredCSV = applyTransforms(allPreferredMap, [
        translateTypes,
        typeMapToCSV,
    ]);

    const adTargetingObject = Object.assign({},
        makeQueryParamsObject(contextTagsCSV, "context"),
        makeQueryParamsObject(allPreferredCSV, "preferred"),
        contextTagTypesCSV
    );

    const referrerProperties = referrer ? { referrer: transformReferrer(referrer) } : {};
    const userAttributeParams = pluckUserAttributeParams(userInfo);

    return Object.assign({}, referrerProperties, adTargetingObject, userAttributeParams);
}

function makeTypeMaps({ userInfo, contextInfo = {} }) {
    userInfo = userInfo || {};
    const { contentTags = [] } = contextInfo;
    // get initial, filtered contextTags. These are used to derive all the params
    const filteredContextTags = applyTransforms(contentTags, [
        filterByType,
        defaultDerivedScore,
        filterByScore,
    ]);

    const contextTagsTypeMap = applyTransforms(filteredContextTags, [
        getTopKTags,
        transformToTypeMap,
    ]);

    const cumulativeScoredContentTagsMap = applyTransforms(contentTags, [
        scoreContentTags,
        getTopKContextTagTypes,
        transformToTypeMap,
    ]);

    const preferredTags = parsePreferenceTags(userInfo);
    // "Topic" types are excluded from being copied over to preferredTags because we sell ad placements to advertisers with
    // the promise that we are targeting "preferred topics" with some confidence.
    // (i.e users have viewed articles with specific topics more than N times)
    // Business would rather not use the currently viewing article to determine the preferred topic
    const combinedTags = combineTags({
        contextTags: filteredContextTags.filter((o) => o.type !== "Topic"),
        preferredTags,
    });
    const preferredTagsTypeMap = applyTransforms(combinedTags, [
        getTopKTags,
        transformToTypeMap,
    ]);

    const preferredAuthors = parsePreferenceAuthors(userInfo);
    const preferredAuthorsMap = applyTransforms(preferredAuthors, [
        getTopKAuthors,
        transformToTypeMap,
    ]);

    const preferredResourceTypes = parsePreferenceResourceTypes(userInfo);
    const preferredResourceTypesMap = applyTransforms(preferredResourceTypes, [
        getTopKResourceTypes,
        transformToTypeMap,
    ]);

    return {
        contextTagsTypeMap,
        preferredTagsTypeMap,
        preferredAuthorsMap,
        preferredResourceTypesMap,
        cumulativeScoredContentTagsMap,
    };
}

function applyTransforms(tags, transforms) {
    return transforms.reduce((currentValue, transform) => transform(currentValue), tags);
}

function makeQueryParamsObject(tags, queryPrefix) {
    const params = {};

    for (const type in tags) {
        if (tags[type]) {
            const queryParamKey = queryPrefix + type;

            params[queryParamKey] = tags[type];
        }
    }

    return params;
}
